import React from 'react';
import { Box, SkeletonGroup, SkeletonRectangle } from '@wix/design-system';
import { useIsMobile } from '../../hooks/useIsMobile';
import { HeroBulletsSkeleton } from './HeroBulletsSkeleton';
import { HERO_BULLETS_PADDING_TOP, HERO_COLUMN_GAP, HERO_TITLE_HEIGHT } from './heroGeometry';
import s from './HeroCard.css';
const DesktopTopRow = _ref => {
  let {
    optimized,
    perception
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    gap: "SP3",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: perception ? HERO_COLUMN_GAP : 'SP2',
    flex: 1,
    minWidth: 0
  }, optimized ? /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "93px",
    height: "24px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "64px",
    height: "24px"
  })) : /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "93px",
    height: "24px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "290px",
    height: perception ? HERO_TITLE_HEIGHT : '40px'
  }), perception ? /*#__PURE__*/React.createElement(Box, {
    maxWidth: "466px",
    paddingTop: HERO_BULLETS_PADDING_TOP
  }, /*#__PURE__*/React.createElement(HeroBulletsSkeleton, null)) : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1",
    maxWidth: "466px",
    paddingTop: "SP2"
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "80%",
    height: "18px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "100%",
    height: "20px"
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP4",
    verticalAlign: "middle",
    flexShrink: 0,
    alignSelf: "flex-end"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1",
    align: "right"
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "115px",
    height: "16px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "138px",
    height: "16px"
  })), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "138px",
    height: "46px"
  })));
};
const MobileTopRow = _ref2 => {
  let {
    optimized
  } = _ref2;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP3",
    width: "100%"
  }, optimized ? /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "68px",
    height: "20px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "50px",
    height: "20px"
  })) : /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "68px",
    height: "20px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "112px",
    height: "24px"
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle",
    gap: "SP3",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "139px",
    height: "22px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "139px",
    height: "22px"
  })), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "78px",
    height: "38px"
  })));
};
export const HeroCardSkeleton = _ref3 => {
  let {
    dataHook = 'hero-card-skeleton',
    optimizedSkeletonLayout = false,
    perception = false
  } = _ref3;
  const isMobile = useIsMobile();
  if (!optimizedSkeletonLayout) {
    return /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      borderRadius: "12px",
      overflow: "hidden",
      padding: "SP6",
      className: s.cardShadow,
      dataHook: dataHook
    }, /*#__PURE__*/React.createElement(SkeletonGroup, {
      skin: "light",
      className: s.skeletonGroup
    }, isMobile ? /*#__PURE__*/React.createElement(MobileTopRow, {
      optimized: false
    }) : /*#__PURE__*/React.createElement(DesktopTopRow, {
      optimized: false,
      perception: perception
    })));
  }
  return /*#__PURE__*/React.createElement(SkeletonGroup, {
    skin: "light",
    className: s.skeletonGroup
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    dataHook: dataHook
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    borderRadius: "12px",
    overflow: "hidden",
    padding: "SP6",
    className: s.card
  }, isMobile ? /*#__PURE__*/React.createElement(MobileTopRow, {
    optimized: true
  }) : /*#__PURE__*/React.createElement(DesktopTopRow, {
    optimized: true,
    perception: perception
  })), /*#__PURE__*/React.createElement(Box, {
    marginTop: "12px",
    marginBottom: "12px",
    width: "100%"
  }, /*#__PURE__*/React.createElement("div", {
    className: s.bannerStripe
  }))));
};
//# sourceMappingURL=HeroCardSkeleton.js.map